import { Text } from '@southfields-digital/mpxlive-components';
import classNames from 'classnames';

import { CardTypes } from 'src/types/matchControl/card';
import { SoccerContestant } from 'src/types/matchControl/contestant';

import { AdjustButton, Cards, ColorSwatch, ExtendedContestant } from '../../components';
import styles from '../../MatchControl.module.scss';
import { MatchControlVariant } from '../../types';

type ScoreRowProps = {
  home: SoccerContestant;
  away: SoccerContestant;
  variant: MatchControlVariant;
  handleChangeScore: (contestant: SoccerContestant, amount: number) => void;
  handleChangeColor: (
    contestant: SoccerContestant,
    color: 'primaryColor' | 'secondaryColor',
    value: string
  ) => void;
  handleChangeCards: (contestant: SoccerContestant, card: CardTypes, amount: number) => void;
  handleChangeShortName: (contestant: SoccerContestant, name: string) => void;
};

const ScoreRow = ({
  home,
  away,
  variant,
  handleChangeScore,
  handleChangeColor,
  handleChangeCards,
  handleChangeShortName,
}: ScoreRowProps) => {
  const isCompact = variant === 'default';

  const homePanel =
    variant === 'extended' ? (
      <ExtendedContestant
        logoUrl={home.imageUrl}
        shortName={home.shortName}
        handleChangeShortName={(name) => handleChangeShortName(home, name)}
      />
    ) : (
      <Cards
        card={CardTypes.RED_CARD}
        teamName={home.shortName}
        amount={home.redCards || 0}
        handleChangeCards={(card, amount) => handleChangeCards(home, card, amount)}
        handleChangeTeamName={(name) => handleChangeShortName(home, name)}
      />
    );

  const awayPanel =
    variant === 'extended' ? (
      <ExtendedContestant
        logoUrl={away.imageUrl}
        shortName={away.shortName}
        handleChangeShortName={(name) => handleChangeShortName(away, name)}
      />
    ) : (
      <Cards
        card={CardTypes.RED_CARD}
        teamName={away.shortName}
        amount={away.redCards || 0}
        handleChangeCards={(card, amount) => handleChangeCards(away, card, amount)}
        handleChangeTeamName={(name) => handleChangeShortName(away, name)}
      />
    );

  const scoreSection = (
    <div className={classNames('flex flex-col gap-6 w-full', isCompact && '!gap-3')}>
      <div className="flex gap-2 justify-around items-center">
        <div className="flex gap-2 flex-1 justify-start items-center">
          <div className="flex">
            <ColorSwatch
              color={home.primaryColor}
              onChange={(color: string) => handleChangeColor(home, 'primaryColor', color)}
              isCompact={isCompact}
            />
            <ColorSwatch
              color={home.secondaryColor}
              onChange={(color: string) => handleChangeColor(home, 'secondaryColor', color)}
              isCompact={isCompact}
            />
          </div>

          <Text
            className={classNames('!text-2xl text-center', isCompact ? 'w-7' : 'w-16')}
            as="h1"
            color="light"
          >
            {home.score?.points || 0}
          </Text>
        </div>
        <Text className="!text-2xl" as="h1" color="light">
          -
        </Text>
        <div className="flex gap-2 flex-1 justify-end items-center">
          <Text
            className={classNames('!text-2xl text-center', isCompact ? 'w-7' : 'w-16')}
            as="h1"
            color="light"
          >
            {away.score?.points || 0}
          </Text>

          <div className="flex">
            <ColorSwatch
              color={away.primaryColor}
              onChange={(color: string) => handleChangeColor(away, 'primaryColor', color)}
              isCompact={isCompact}
            />
            <ColorSwatch
              color={away.secondaryColor}
              onChange={(color: string) => handleChangeColor(away, 'secondaryColor', color)}
              isCompact={isCompact}
            />
          </div>
        </div>
      </div>

      <div className={styles.CurrentScoreWrapper}>
        <div className="flex flex-col gap-6">
          <div className="flex gap-3 items-center">
            <AdjustButton
              onClick={() => handleChangeScore(home, Math.min((home.score?.points || 0) - 1, 0))}
              icon="minus"
              title="Subtract score for home team"
              disabled={home.score?.points < 1}
              isCompact={isCompact}
            />
            <AdjustButton
              onClick={() => handleChangeScore(home, (home.score?.points || 0) + 1)}
              icon="plus"
              title="Add score for home team"
              isCompact={isCompact}
            />
          </div>
          {variant === 'extended' && (
            <Cards
              card={CardTypes.RED_CARD}
              variant={variant}
              amount={home.redCards || 0}
              handleChangeCards={(card, amount) => handleChangeCards(home, card, amount)}
            />
          )}
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex gap-3 items-center">
            <AdjustButton
              onClick={() => handleChangeScore(away, Math.min((away.score?.points || 0) - 1, 0))}
              icon="minus"
              title="Subtract score for away team"
              disabled={away.score?.points < 1}
              isCompact={isCompact}
            />
            <AdjustButton
              onClick={() => handleChangeScore(away, (away.score?.points || 0) + 1)}
              icon="plus"
              title="Add score for away team"
              isCompact={isCompact}
            />
          </div>
          {variant === 'extended' && (
            <Cards
              card={CardTypes.RED_CARD}
              variant={variant}
              amount={away.redCards || 0}
              handleChangeCards={(card, amount) => handleChangeCards(away, card, amount)}
            />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {homePanel}
      {scoreSection}
      {awayPanel}
    </>
  );
};

export default ScoreRow;
